/**
 * Date-only parsing for cross-client consistency.
 *
 * No DOM / Node / Firebase imports — React-Native / Metro-bundlable per the `@elara/domain`
 * portability guard, so web and native bucket calendar dates identically.
 */

/**
 * Parses a date-only string (`'YYYY-MM-DD'`, or a datetime whose date part is `'YYYY-MM-DD'`)
 * into a `Date` at **local** midnight (00:00:00 in the runtime's own time zone).
 *
 * ## Why this exists — the recurring divergence trap
 *
 * `new Date('2024-01-15')` parses the bare date as **UTC** midnight, whereas
 * `new Date('2024-01-15T09:00')` parses as **local** time. That asymmetry means a native
 * client that buckets a date-only string with the plain `Date` constructor lands on the
 * **previous calendar day** for any user west of UTC (all of the Americas) once UTC midnight
 * is still "yesterday" locally. The web client sidesteps this by splitting the string into
 * numeric components and calling `new Date(year, monthIndex, day)`, which is always interpreted
 * in local time.
 *
 * Every cross-client surface that keys off the **calendar day** — itinerary day-keys, trip
 * date partitioning, days-until-departure — MUST normalize date-only strings to local
 * start-of-day so web and native agree. Use this helper instead of `new Date(str)` for those.
 * (Instant-precise values that already carry a time + offset, e.g. flight departure timestamps,
 * are not date-only and should keep their own parsing.)
 *
 * @param dateStr A `'YYYY-MM-DD'` string, optionally followed by `T…` or ` …` (the suffix is ignored).
 * @returns A `Date` at local midnight, or `undefined` if the date part is missing or malformed.
 *
 * @example
 * // In a UTC-5 (Americas) runtime:
 * parseDateOnlyLocal('2024-01-15');          // Jan 15 2024 00:00 local  ✅
 * new Date('2024-01-15');                     // Jan 14 2024 19:00 local  ❌ (UTC midnight)
 */
export function parseDateOnlyLocal(dateStr: string | null | undefined): Date | undefined {
  if (!dateStr) return undefined;
  // `split` always yields at least one element, so `[0]` is safe. A missing/malformed component
  // parses to `NaN` or `0` (both falsy) and is rejected by the guard below — no separate NaN check.
  const [year, month, day] = dateStr.split(/[T ]/)[0]!.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}
